// =============================================
// System Admin Login Page
// Kipikid - מערכת נוכחות ילדים בגנים
// =============================================

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, User, Eye, EyeOff, AlertCircle, Shield } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { adminApi } from '../../services/api';
import FormLabel from '../../components/common/FormLabel';
import { loginHelpTexts } from '../../constants/helpTexts';
import { useLoginForm } from '../../hooks/useLoginForm';
import { sysAdminColors as colors } from '../../styles/colors';

export default function SysAdminLogin() {
  const navigate = useNavigate();
  const { setUser, setToken, setRefreshToken, setTokenExpiresAt, setRequiresAgreement } = useAuthStore();

  const handleLogin = async (username: string, password: string) => {
    const result = await adminApi.adminLogin(username, password);

    if (result.success && result.data) {
      const authData = result.data;
      const serverRole = authData.user?.role || '';

      // רק SystemAdmin מורשה להיכנס לממשק מנהל מערכת
      if (serverRole !== 'SystemAdmin') {
        throw new Error('אין הרשאת מנהל מערכת. יש להיכנס דרך ממשק מנהל.');
      }

      setUser({
        id: authData.user?.id?.toString() || '',
        email: username,
        name: `${authData.user?.firstName || ''} ${authData.user?.lastName || ''}`.trim(),
        firstName: authData.user?.firstName,
        lastName: authData.user?.lastName,
        phone: authData.user?.phone,
        role: 'sysadmin',
        publicId: authData.user?.publicId,
      });

      setToken(authData.accessToken);
      if (authData.refreshToken) setRefreshToken(authData.refreshToken);
      if (authData.expiresAt) setTokenExpiresAt(authData.expiresAt);

      if (authData.requiresAgreement) {
        setRequiresAgreement(true, authData.agreementVersion);
        navigate('/agreement-approval', { replace: true });
        return;
      }

      navigate('/sysadmin/dashboard');
    } else {
      throw new Error(result.message || 'שם משתמש או סיסמה שגויים');
    }
  };

  const {
    username, setUsername,
    password, setPassword,
    showPassword, setShowPassword,
    error,
    isLoading,
    handleSubmit,
  } = useLoginForm(handleLogin);

  useEffect(() => {
    document.title = 'קיפיקיד — מנהל מערכת';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#7c3aed');
  }, []);

  const inputStyle = {
    width: '100%',
    padding: '14px 44px',
    fontSize: '15px',
    border: `2px solid ${colors.border}`,
    borderRadius: '12px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    direction: 'rtl' as const,
    transition: 'border-color 0.2s',
  };

  return (
    <div
      dir="rtl"
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 50%, #ddd6fe 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '24px',
          boxShadow: '0 20px 60px rgba(124, 58, 237, 0.15)',
          width: '100%',
          maxWidth: '400px',
          padding: '40px 32px',
        }}
      >
        {/* Logo & Title */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Shield size={40} color="white" />
          </div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: colors.primary, marginBottom: '4px' }}>
            מנהל מערכת
          </h1>
          <p style={{ fontSize: '14px', color: colors.textLight }}>
            התחברות לממשק מנהל מערכת
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 16px',
              backgroundColor: '#fef2f2',
              borderRadius: '10px',
              marginBottom: '20px',
            }}
          >
            <AlertCircle size={18} color={colors.error} />
            <span style={{ fontSize: '14px', color: colors.error }}>{error}</span>
          </motion.div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Username */}
          <div>
            <FormLabel text="שם משתמש" helpText={loginHelpTexts.username.tooltip} />
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="שם משתמש"
                style={inputStyle}
                disabled={isLoading}
              />
              <User
                size={18}
                color={colors.textLight}
                style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)' }}
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <FormLabel text="סיסמה" helpText={loginHelpTexts.password.tooltip} />
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="סיסמה"
                style={inputStyle}
                disabled={isLoading}
              />
              <Lock
                size={18}
                color={colors.textLight}
                style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  left: '14px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                {showPassword ? (
                  <EyeOff size={18} color={colors.textLight} />
                ) : (
                  <Eye size={18} color={colors.textLight} />
                )}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <motion.button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '16px',
              marginTop: '8px',
              borderRadius: '12px',
              border: 'none',
              background: `linear-gradient(135deg, ${colors.primary} 0%, #6d28d9 100%)`,
              color: 'white',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
            }}
            whileHover={{ scale: isLoading ? 1 : 1.02 }}
            whileTap={{ scale: isLoading ? 1 : 0.98 }}
          >
            {isLoading ? 'מתחבר...' : 'התחברות'}
          </motion.button>
        </form>

        {/* Back Link */}
        <button
          onClick={() => navigate('/')}
          style={{
            width: '100%',
            marginTop: '20px',
            padding: '12px',
            backgroundColor: 'transparent',
            color: colors.textLight,
            border: 'none',
            fontSize: '14px',
            cursor: 'pointer',
          }}
        >
          חזרה לדף הבית
        </button>
      </motion.div>
    </div>
  );
}
